import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { EditModeProvider } from "./contexts/EditModeContext";
import { GuestProvider } from "./contexts/GuestContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import Home from "./pages/Home";
import HomeLegacy from "./pages/HomeLegacy";
import GalleryPage from "./pages/GalleryPage";
import GuestLoginPage from "./pages/GuestLoginPage";
import GuestPage from "./pages/GuestPage";
import AdminGuestsPage from "./pages/AdminGuestsPage";
import LockIcon from "./components/LockIcon";
import "./styles/globals.css";

function App() {
  return (
    <Router>
      <LanguageProvider>
        <AppContent />
      </LanguageProvider>
    </Router>
  );
}

function AppContent() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { lang } = useLanguage();

  useEffect(() => {
    fetchContent();
  }, [lang]);

  const fetchContent = () => {
    setLoading(true);
    fetch(`/api/content?lang=${lang}`)
      .then((res) => res.json())
      .then((data) => {
        setContent(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading content:", err);
        setLoading(false);
      });
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <EditModeProvider>
      <GuestProvider>
        <div className="App">
            <Routes>
              {/* English Routes */}
              <Route path="/" element={<Home content={content} setContent={setContent} />} />
              <Route path="/legacy" element={<HomeLegacy content={content} setContent={setContent} />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/gallery/:albumId" element={<GalleryPage />} />
              <Route path="/guest/login" element={<GuestLoginPage />} />
              <Route path="/guest" element={<GuestPage />} />
              <Route path="/admin/guests" element={<AdminGuestsPage />} />

              {/* Lithuanian Routes */}
              <Route path="/lt" element={<Home content={content} setContent={setContent} />} />
              <Route path="/lt/legacy" element={<HomeLegacy content={content} setContent={setContent} />} />
              <Route path="/lt/gallery" element={<GalleryPage />} />
              <Route path="/lt/gallery/:albumId" element={<GalleryPage />} />
              <Route path="/lt/guest/login" element={<GuestLoginPage />} />
              <Route path="/lt/guest" element={<GuestPage />} />
              <Route path="/lt/admin/guests" element={<AdminGuestsPage />} />
            </Routes>
            <LockIcon />

            {/* Global SVG Filters for 3D Handmade Paper Textures with peaks and shadows */}
            <svg style={{ position: "absolute", width: "1px", height: "1px", opacity: 0, pointerEvents: "none", overflow: "hidden" }}>
              <defs>
                <filter id="handmade-paper" x="0%" y="0%" width="100%" height="100%">
                  {/* 1. Generate organic fractal noise representing pulp density */}
                  <feTurbulence 
                    type="fractalNoise" 
                    baseFrequency="0.045" 
                    numOctaves="3" 
                    result="noise" 
                  />
                  {/* 2. Tuned contrast shift for visible but elegant pulp peaks and valleys */}
                  <feColorMatrix 
                    type="matrix" 
                    values="1 0 0 0 0
                            0 1 0 0 0
                            0 0 1 0 0
                            0 0 0 1.4 -0.15" 
                    result="soft-contrast-noise" 
                  />
                  {/* 3. Render 3D shading with balanced surfaceScale and elevation for mobile high-DPI screens */}
                  <feDiffuseLighting 
                    in="soft-contrast-noise" 
                    lighting-color="#ffffff" 
                    surfaceScale="1.15" 
                    result="light-shading"
                  >
                    <feDistantLight azimuth="135" elevation="64" />
                  </feDiffuseLighting>
                  {/* 4. Blend the light shader onto the original background card color */}
                  <feBlend mode="multiply" in="SourceGraphic" in2="light-shading" result="blended-output" />
                  {/* 5. Mask output strictly to SourceGraphic's alpha boundaries (preserves transparency and border-radius!) */}
                  <feComposite in="blended-output" in2="SourceGraphic" operator="in" />
                </filter>
              </defs>
            </svg>
          </div>
      </GuestProvider>
    </EditModeProvider>
  );
}

export default App;
